/**
 * Base Authentication Provider
 * Abstract class that all auth providers must extend
 */
export class BaseAuthProvider {
    /**
     * Determines if this provider is enabled based on context
     * Each provider implements its own logic considering:
     * - .env configuration (VUE_APP_AUTH_PROVIDERS)
     * - Runtime backend config (appConfig)
     * - Subdomain-specific rules
     *
     * @param {Object} context - Context for decision
     * @param {Array<string>} context.envProviders - Providers from VUE_APP_AUTH_PROVIDERS
     * @param {string} context.subdomain - Current subdomain (e.g., 'photos', 'juventus')
     * @param {Object} context.appConfig - Backend configuration
     * @param {boolean} context.appConfig.disable_social_login - If social login disabled
     * @returns {boolean} Whether this provider is enabled
     */
    isEnabled(context) {
        // Default implementation: check if present in .env
        return context.envProviders.includes(this.getMethod())
    }

    /**
     * Get the provider method identifier
     * Must be implemented by each provider
     * @returns {string} Provider identifier (e.g., 'password', 'google')
     */
    getMethod() {
        throw new Error('Provider must implement getMethod()')
    }

    /**
     * Authenticate using this provider
     * Must be implemented by each provider
     *
     * @param {Object} credentials - Provider-specific credentials
     * @returns {Promise<Object>} Authentication data with at minimum:
     *   - token: string (REQUIRED) - Authentication token
     *   - tokenType: string (REQUIRED) - Token type ('Token', 'Bearer', etc.)
     *   - ...metadata: any additional data from backend (e.g., picacode for SSO)
     *
     * IMPORTANT: Providers should return ALL data from backend API responses
     * using spread operator (...response.data) to preserve metadata like picacode,
     * user preferences, or any future fields the backend might add.
     */
    async authenticate(credentials) {
        throw new Error('Provider must implement authenticate()')
    }

    /**
     * Logout using this provider (optional)
     * Override if provider needs server-side cleanup
     * @param {Object} tokenData - Current token data
     * @returns {Promise<void>}
     */
    async logout(tokenData) {
        // Optional: override if needed
        return Promise.resolve()
    }

    /**
     * Whether this provider supports token refresh
     * @returns {boolean}
     */
    supportsRefresh() {
        return false
    }

    /**
     * Refresh token (if supported)
     * @param {Object} tokenData - Current token data
     * @returns {Promise<Object>} New token data
     */
    async refreshToken(tokenData) {
        throw new Error('This provider does not support token refresh')
    }

    /**
     * Get alternative providers to suggest to user
     * @param {Array<string>} enabledProviders - Currently enabled providers
     * @returns {Array<string>} List of alternative provider methods
     */
    getAlternatives(enabledProviders) {
        // Default: no alternatives
        // Override in specific providers to suggest alternatives
        return []
    }
}

export default BaseAuthProvider
